import type { Board } from "../board";
import { getMobGoldValue } from "../economy/mobGoldValues";
import type { DamageType } from "../damageType";
import { PRIORITY_LAST, type ProcessableThing, type SomethingWithState } from "../interfaces";
import type { SimpleBoard } from "../levelgenerator/simpleBoard";
import { Mob } from "../mob";
import { DestroyableObstacle } from "../destroyableObstacle";
import { ParticleType } from "../particleType";
import type { PhysicalThing } from "../physicalThing";
import { deserializeXY, getMobFactory } from "../serialization";
import type { Thing } from "../thing";
import type { XY } from "../xy";
import { GoldDrop } from "./goldDrop";
import { MobSpawnerConfig } from "./portal2/mobSpawnerConfig";

export enum RobotSpawnerState {
	SPAWNING = "SPAWNING",
	CLOSED = "CLOSED",
	OPEN = "OPEN",
}

export class RobotSpawner extends DestroyableObstacle implements ProcessableThing, SomethingWithState {
	static key = "RobotSpawner";
	private spawnerActive: boolean;
	private state: RobotSpawnerState = RobotSpawnerState.CLOSED;
	private mobSpawnerConfig: MobSpawnerConfig | null;
	private limitMobsRadius: number = 10;
	private limitMobsCount: number = 14;
	gold: number = 0;

	constructor(xy: XY, active: boolean = true, mobSpawnerConfig?: MobSpawnerConfig) {
		super(xy);
		this.spawnerActive = active;
		this.mobSpawnerConfig = mobSpawnerConfig ?? null;
	}

	getBaseGoldValue(): number {
		return getMobGoldValue(RobotSpawner.key);
	}

	getDebrisParticleType(): ParticleType {
		return ParticleType.METAL_DEBRIS;
	}

	static override initOnBoard(xy: XY, board: SimpleBoard, props?: Record<string, unknown>): PhysicalThing[] {
		const active = (props?.active as boolean) ?? true;
		let config: MobSpawnerConfig | undefined;
		if (props?.spawns) {
			config = new MobSpawnerConfig();
			config.addEntry({ mobType: props.spawns as string, share: 1 });
		}
		const spawner = new RobotSpawner(xy, active, config);
		board.putThing(spawner);
		return [spawner];
	}

	processTurn(board: Board): void {
		if (!this.spawnerActive) return;

		this.transitionState();

		if (this.state === RobotSpawnerState.OPEN && this.countMobsAround(board) > this.limitMobsCount) {
			this.state = RobotSpawnerState.CLOSED;
			return;
		}

		if (this.state !== RobotSpawnerState.SPAWNING) return;
		if (board.hasMob(this.xy)) return;

		let mob: Mob;
		if (this.mobSpawnerConfig) {
			const m = this.mobSpawnerConfig.getMobToSpawn();
			if (!m) return;
			mob = m;
		} else {
			mob = getMobFactory("Robot")();
		}
		mob.xy = this.xy;
		board.addThing(mob);
		mob.gold = this.board!.spendGoldBudget(mob.getBaseGoldValue());
	}

	private countMobsAround(board: Board): number {
		let count = 0;
		for (const thing of board.getPhysicalThings()) {
			if (thing instanceof Mob && thing.active && thing.xy.isInSquareRadius(this.xy, this.limitMobsRadius)) {
				count++;
			}
		}
		return count;
	}

	private transitionState(): void {
		const rand = Math.random();
		switch (this.state) {
			case RobotSpawnerState.SPAWNING:
				this.state = rand < 0.3 ? RobotSpawnerState.CLOSED : RobotSpawnerState.SPAWNING;
				break;
			case RobotSpawnerState.CLOSED:
				this.state = rand < 0.045 ? RobotSpawnerState.OPEN : RobotSpawnerState.CLOSED;
				break;
			case RobotSpawnerState.OPEN:
				this.state = RobotSpawnerState.SPAWNING;
				break;
		}
	}

	setActive(active: boolean): void {
		this.spawnerActive = active;
	}

	protected override onZeroHp(source: Thing | null, type: DamageType | null): void {
		if (this.board) this.board.stats.recordSpawnerDestroyed();
		if (this.gold > 0 && this.board) {
			GoldDrop.spawnOrMerge(this.board, this.xy, this.gold);
		}
		super.onZeroHp(source, type);
	}

	setMobSpawnerConfig(config: MobSpawnerConfig): void {
		this.mobSpawnerConfig = config;
	}

	getProcessPriority(): number {
		return PRIORITY_LAST;
	}

	getState(): RobotSpawnerState {
		return this.state;
	}

	serialize(): Record<string, unknown> {
		const data: Record<string, unknown> = {
			...super.serialize(),
			state: this.state,
			spawnerActive: this.spawnerActive,
			mobSpawnerConfig: this.mobSpawnerConfig?.serialize() ?? null,
		};
		if (this.gold > 0) data.gold = this.gold;
		return data;
	}

	static deserialize(data: any): RobotSpawner {
		const config = data.mobSpawnerConfig ? MobSpawnerConfig.deserialize(data.mobSpawnerConfig) : undefined;
		const s = new RobotSpawner(deserializeXY(data.xy), data.spawnerActive, config);
		s.restoreBaseFields(data);
		s.state = data.state ?? RobotSpawnerState.CLOSED;
		s.gold = data.gold ?? 0;
		return s;
	}
}
